import type React from 'react'
import type { SRT_Header, SRT_RowData, SRT_TableInstance } from '../../types'

import { ArrowDownIcon } from 'lucide-react'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'

import { cn } from '../../lib/utils'

export type SRT_TableHeadCellSortLabelProps<TData extends SRT_RowData> = {
	header: SRT_Header<TData>
	table: SRT_TableInstance<TData>
} & React.ComponentProps<'button'>

export const SRT_TableHeadCellSortLabel = <TData extends SRT_RowData>({
	header,
	table,
	...rest
}: SRT_TableHeadCellSortLabelProps<TData>) => {
	const {
		getState,
		options: { localization },
	} = table
	const { column } = header
	const { columnDef } = column
	const { isLoading, showSkeletons, sorting } = getState()

	const isSorted = !!column.getIsSorted()
	const sortDirection = column.getIsSorted()

	const sortTooltip =
		isLoading || showSkeletons
			? ''
			: column.getIsSorted()
				? column.getIsSorted() === 'desc'
					? localization.sortedByColumnDesc.replace(
							'{column}',
							columnDef.header
						)
					: localization.sortedByColumnAsc.replace('{column}', columnDef.header)
				: column.getNextSortingOrder() === 'desc'
					? localization.sortByColumnDesc.replace('{column}', columnDef.header)
					: localization.sortByColumnAsc.replace('{column}', columnDef.header)

	if (!isSorted) {
		return null
	}

	return (
		<>
			<Tooltip>
				<TooltipTrigger asChild>
					<Button
						size='icon-xs'
						variant='ghost'
						aria-label={sortTooltip}
						onClick={(e) => {
							e.stopPropagation()
							header.column.getToggleSortingHandler()?.(e)
						}}
						{...rest}
					>
						<ArrowDownIcon
							className={cn(
								'transition-transform',
								sortDirection === 'desc' ? 'rotate-0' : 'rotate-180'
							)}
						/>
					</Button>
				</TooltipTrigger>
				<TooltipContent>{sortTooltip}</TooltipContent>
			</Tooltip>
			{sorting.length > 1 && column.getSortIndex() !== -1 && (
				<Badge
					size='sm'
					className='absolute top-0.5 right-0.5 z-1 flex size-3 min-h-4 min-w-4 items-center justify-center rounded-full p-0 text-[9px]'
				>
					{column.getSortIndex() + 1}
				</Badge>
			)}
		</>
	)
}
